import { useMemo } from "react";
import NoteItem from "core/components/NoteItem";
import Image from "core/components/Image";
import ShareComponent from "core/components/Share";
import { getYouTubeThumbnail } from "core/utils/helper";
import Icon from "core/assets/icons";
import { _t } from "core/language";
import { type IRecipeDetail } from './../store/types'
import { useUpdateFavorite } from "./../hooks/recipe";
import { Swiper, SwiperSlide } from "swiper/react";

const RecipeInfo = ({
  recipe,
  loading,
  reload,
  recipeRequestId,
}: {
  recipe: IRecipeDetail,
  loading: boolean,
  reload?: (cb: any) => void,
  recipeRequestId?: string | number | null | undefined,
}) => {
  const onUpdateFavorite = useUpdateFavorite()

  const videoIds = useMemo(() => {
    return recipe.youtube_videos?.map(
      (video: IRecipeDetail['youtube_videos'][number]) => video.videoId
    ) || []
  }, [recipe])
  
  const videoIdsUnique = useMemo(() => {
    return Array.from(new Set([recipe.video_id, ...videoIds]))
  }, [videoIds, recipe.video_id])

  return (
    <div>
      <Swiper spaceBetween={10}>
        {videoIdsUnique.map((video_id: string, key: number) => (
          <SwiperSlide key={key}>
            <Image
              src={getYouTubeThumbnail(video_id)}
              alt={recipe.name}
              className="object-cover w-full aspect-[328/184] rounded-lg"
            />
          </SwiperSlide>
        ))}
      </Swiper>
      
      <div className="flex justify-between mt-2.5">
        <label className="text-[17px] font-bold text-[#111111]">
          {recipe.name}
        </label>
        {!loading ? <div className="flex gap-4 relative">
          <NoteItem
            reload={reload}
            recipeRequestId={recipeRequestId}
            id={recipe.id}
            active={!!recipe.is_favorite}
            onChange={onUpdateFavorite}
            type="hasText"
            className="flex flex-col items-center min-w-6"
          />
          <div className="flex flex-col items-center min-w-6">
            <img src={Icon.RecipeTime} alt="RecipeTime" />
            <label
              className="text-[9px] block mt-1 font-medium whitespace-nowrap"
            >
              {recipe.content?.cooking_duration}
            </label>
          </div>
          <div className="flex flex-col items-center min-w-6">
            <img src={Icon.RecipeCalories} alt="RecipeCalories" />
            <label
              className="text-[9px] block mt-1 font-medium whitespace-nowrap"
            >
              {recipe.content?.calories}
            </label>
          </div>
          <div className="flex flex-col items-center min-w-6">
            <ShareComponent
              id={recipe.id}
              title={recipe.name}
              urlImg={getYouTubeThumbnail(recipe?.video_id) || Icon.NoImage}
              recipeRequestId={recipeRequestId}
              post={recipe}
            />
            <label
              className="text-[9px] block mt-1 font-medium whitespace-nowrap"
            >
              {_t('recipe.share')}
            </label>
          </div>
        </div> : null}
      </div>
      <div className="rounded-lg bg-[#F4F4F4] border-[#333] p-3.5 mt-3.5 text-[13px] tracking-tighter">
        {loading ? (
          <div className="animate-pulse">
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-3 bg-gray-300 rounded col-span-3"></div>
                <div className="h-3 bg-gray-300 rounded col-span-2"></div>
              </div>
            </div>
          </div>
        ) : recipe.content?.description}
      </div>
      <div className="text-[11px] tracking-tighter mt-3">
        {loading ? (
          <div className="animate-pulse">
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-3 bg-gray-300 rounded col-span-3"></div>
                <div className="h-3 bg-gray-300 rounded col-span-2"></div>
              </div>
            </div>
          </div>
        ) : <div>
          ※ {_t('recipe.calories_description', {calories: recipe?.content?.calories || '0'})}
        </div>}
      </div>
    </div>
  )
}

export default RecipeInfo
